/*!
 * lh-card-swipe.js — Mobile product card media swipe carousel
 *
 * Architecture: transform-based drag, non-passive touchmove
 * ─────────────────────────────────────────────────────────────────────────
 * touch-action is NOT set in CSS. Instead touchmove runs { passive: false }
 * so we can call preventDefault() for confirmed-horizontal drags, which:
 *   (a) stops iOS from scrolling the page during a card swipe
 *   (b) cancels the <a> tap activation (no teal highlight, no navigation)
 *
 * Direction is determined on the first significant movement:
 *   |dx| > |dy|  → horizontal: own the gesture, update translateX
 *   |dy| >= |dx| → vertical:   abort silently, page scroll continues
 *
 * A tap (no movement) never calls preventDefault, so the <a> click fires
 * and navigates to the PDP normally.
 *
 * Root cause of iOS intercept: the Stiletto theme renders a static image
 * link (<a class="lh-card-desktop-link">) as a sibling of .lh-swipe inside
 * .product-item__media. On iOS the @media(pointer:coarse) CSS hide can be
 * stale-cached, so we also disable it in JS on mount.
 */
(function () {
  'use strict';

  var THRESHOLD = 0.20; // advance if drag exceeds 20 % of card width
  var SNAP_MS   = 260;
  var SNAP_EASE = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';

  function mount(el) {
    if (el._lhSwipe) return;

    var track  = el.querySelector('.lh-swipe__track');
    var bars   = el.querySelectorAll('.lh-swipe__bar');
    var slides = el.querySelectorAll('.lh-swipe__slide');
    var n      = slides.length;

    if (!track || n < 2) return;
    var w = el.offsetWidth;
    if (!w) return;

    el._lhSwipe = true;

    var cur      = 0;
    var startX   = 0;
    var startY   = 0;
    var dragX    = 0;
    var active   = false;
    var decided  = false;

    /* Disable the static desktop image link so it never intercepts touches */
    var desktopLink = el.parentElement && el.parentElement.querySelector('.lh-card-desktop-link');
    if (desktopLink) {
      desktopLink.style.pointerEvents = 'none';
      desktopLink.style.display = 'none';
    }

    /* iOS Safari: container with touchstart handlers blocks child <a> first-tap.
       An empty click listener marks the container as interactive, fixing it. */
    el.addEventListener('click', function () {});

    /* ── Lazy image loading ── */
    function loadSlide(idx) {
      if (idx < 0 || idx >= n) return;
      var img = slides[idx].querySelector('img[data-src]');
      if (!img) return;
      img.src = img.getAttribute('data-src');
      var ds = img.getAttribute('data-srcset');
      if (ds) img.srcset = ds;
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }

    /* ── Video helpers ── */
    function getVideo(idx) {
      return slides[idx] ? slides[idx].querySelector('[data-lh-video]') : null;
    }
    function playVideo(idx) {
      var v = getVideo(idx);
      if (v) v.play().catch(function () {});
    }
    function pauseVideo(idx) {
      var v = getVideo(idx);
      if (v) { v.pause(); v.currentTime = 0; }
    }

    /* ── Bar sync ── */
    function syncBars(idx) {
      for (var i = 0; i < bars.length; i++) {
        bars[i].classList.toggle('is-on', i === idx);
      }
    }

    /* ── Snap to a slide with optional animation ── */
    function snapTo(idx, animate) {
      if (idx < 0) idx = 0;
      if (idx >= n) idx = n - 1;
      if (idx !== cur) {
        pauseVideo(cur);
        cur = idx;
        playVideo(cur);
        loadSlide(cur - 1);
        loadSlide(cur);
        loadSlide(cur + 1);
        syncBars(cur);
      }
      w = el.offsetWidth || w;
      track.style.transition = animate
        ? 'transform ' + SNAP_MS + 'ms ' + SNAP_EASE
        : 'none';
      track.style.transform = 'translateX(' + -(cur * w) + 'px)';
    }

    /* ── touchstart — record finger position ── */
    el.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      w       = el.offsetWidth || w;
      startX  = e.touches[0].clientX;
      startY  = e.touches[0].clientY;
      dragX   = 0;
      active  = true;
      decided = false;
      track.style.transition = 'none';
    }, { passive: true });

    /* ── touchmove — non-passive so we can preventDefault for horizontal ── */
    el.addEventListener('touchmove', function (e) {
      if (!active) return;

      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;

      if (!decided) {
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          active = false;
          return;
        }
        decided = true;
      }

      e.preventDefault();
      dragX = dx;

      var resistance = ((cur === 0 && dx > 0) || (cur === n - 1 && dx < 0))
        ? 0.25 : 1;
      track.style.transform =
        'translateX(' + (-(cur * w) + dx * resistance) + 'px)';
    }, { passive: false });

    /* ── touchend — snap to target slide ── */
    el.addEventListener('touchend', function (e) {
      if (!active) return;
      active  = false;
      decided = false;

      var moved = Math.abs(dragX);

      if (moved > 15) {
        /* Real swipe: absorb the follow-up click, advance or retreat */
        el.addEventListener('click', function absorb(ev) {
          ev.preventDefault();
          ev.stopPropagation();
        }, { once: true, capture: true });
        var dx     = startX - e.changedTouches[0].clientX;
        var target = cur;
        if (Math.abs(dx) > w * THRESHOLD) target = cur + (dx > 0 ? 1 : -1);
        snapTo(target, true);
      } else if (moved > 2) {
        /* Slight drift: snap back */
        snapTo(cur, true);
      } else {
        /* Pure tap: navigate directly — bypasses iOS "hover-then-click" intercept
           where first tap activates :hover state and second tap follows the link. */
        var href = slides[cur] && slides[cur].getAttribute('href');
        if (href) {
          el.addEventListener('click', function absorb(ev) {
            ev.preventDefault();
            ev.stopPropagation();
          }, { once: true, capture: true });
          window.location.href = href;
        }
      }
    }, { passive: true });

    /* ── touchcancel — snap back cleanly ── */
    el.addEventListener('touchcancel', function () {
      if (!active) return;
      active  = false;
      decided = false;
      snapTo(cur, true);
    }, { passive: true });

    /* ── IntersectionObserver: play/pause video when card enters viewport ── */
    if (typeof IntersectionObserver !== 'undefined') {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { playVideo(cur); }
          else { pauseVideo(cur); }
        });
      }, { threshold: 0.1 }).observe(el);
    }

    /* ── Init ── */
    track.style.transition = 'none';
    track.style.transform  = 'translateX(0)';
    syncBars(0);
    loadSlide(1);
  }

  function initAll() {
    var els = document.querySelectorAll('.lh-swipe');
    for (var i = 0; i < els.length; i++) mount(els[i]);
  }

  var _pending = false;
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function () {
      if (_pending) return;
      _pending = true;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { _pending = false; initAll(); });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function readyInit() {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { initAll(); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', readyInit);
  } else {
    readyInit();
  }

  window.addEventListener('load', initAll);
  document.addEventListener('shopify:section:load', function () {
    setTimeout(initAll, 100);
  });
  window.lhInitCardSwipe = initAll;

})();
